// src/pages/UbicacionVista.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Pagination,
  FormControlLabel,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  AccessTime,
  Check as CheckIcon,
  MoreVert as MoreVertIcon,
  StopCircle as StopCircleIcon,
} from '@mui/icons-material';
import api from '../api/api';

const ROWS_PER_PAGE = 10;
const VISIBILITY_HOURS = 3;

// === DECODIFICAR JWT ===
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decodificando token:', e);
    return null;
  }
};

// Parsea timestamp del backend evitando desfase UTC→local
const parseTimestamp = (ts) => {
  if (!ts) return new Date();
  if (typeof ts === 'string') {
    // Si ya trae zona horaria explícita, Date() la maneja sola
    if (ts.includes('+') || ts.match(/-\d{2}:\d{2}$/)) {
      return new Date(ts);
    }
    // Sin zona → forzar lectura como hora local (quitar T evita que JS asuma UTC)
    return new Date(ts.split('.')[0].replace('T', ' '));
  }
  return new Date(ts);
};

const formatTime = (timestamp) => {
  if (!timestamp) return '---';
  const date = timestamp instanceof Date ? timestamp : parseTimestamp(timestamp);
  return date.toLocaleTimeString('es-SV', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const UbicacionVista = () => {
  const token = localStorage.getItem('token');
  const decodedToken = decodeToken(token);
  const SUCURSAL_ID = decodedToken?.id_sucursal || null;
  const SUCURSAL_NAME = decodedToken?.nombre_sucursal || 'Sucursal desconocida';

  const [ubicacionesFiltradas, setUbicacionesFiltradas] = useState([]);
  const [rutas, setRutas] = useState({});
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('warning');
  const [currentPage, setCurrentPage] = useState(0);
  const [modoManual, setModoManual] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPaciente, setMenuPaciente] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPaciente, setDialogPaciente] = useState(null);
  const [finalizando, setFinalizando] = useState(false);

  const showSnackbar = (msg, severity = 'warning') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // === FETCH PRINCIPAL ===
  const fetchData = async () => {
    if (!SUCURSAL_ID) {
      showSnackbar('Error: No se pudo determinar la sucursal del usuario.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const q = `?id_sucursal=${SUCURSAL_ID}`;

      // 4 fuentes de datos en paralelo
      const [ubicRes, salasRes, activosRes, finalizadosRes] = await Promise.all([
        api.get(`/pacientes/ubicaciones${q}`),
        api.get(`/pacientes/salas${q}`),
        api.get(`/pacientes/rutas-activas${q}`).catch(() => ({ data: [] })),
        api.get(`/pacientes/rutas-finalizadas-recientes${q}`).catch(() => ({ data: [] })),
      ]);

      const rawUbicaciones    = ubicRes.data       || [];
      const salasDisponibles  = salasRes.data       || [];
      const pacientesActivos  = activosRes.data     || [];
      const pacientesFinalizados = finalizadosRes.data || [];

      // ── Mapa base: primero activos ──
      const ultimaPorPaciente = {};

      pacientesActivos.forEach((p) => {
        if (!ultimaPorPaciente[p.qr_code]) {
          ultimaPorPaciente[p.qr_code] = {
            ...p,
            timestamp: parseTimestamp(p.timestamp),
          };
        }
      });

      // ── Agregar finalizados recientes (los que no estén ya en el mapa) ──
      pacientesFinalizados.forEach((p) => {
        if (!ultimaPorPaciente[p.qr_code]) {
          ultimaPorPaciente[p.qr_code] = {
            ...p,
            // created_at es la referencia temporal para finalizados sin escaneo
            timestamp: parseTimestamp(p.created_at || p.timestamp),
          };
        }
      });

      // ── Sobreescribir con escaneos físicos reales (tienen prioridad de hora) ──
      rawUbicaciones.forEach((u) => {
        const ts = parseTimestamp(u.timestamp);
        const cleanedSala = u.sala ? u.sala.trim() : null;
        if (!ultimaPorPaciente[u.qr_code] || ts > ultimaPorPaciente[u.qr_code].timestamp) {
          ultimaPorPaciente[u.qr_code] = {
            ...ultimaPorPaciente[u.qr_code],
            ...u,
            sala: cleanedSala,
            timestamp: ts,
          };
        }
      });

      const ubicacionesCompletas = Object.values(ultimaPorPaciente);

      // ── Cargar ruta individual por paciente ──
      const rutasData = await Promise.all(
        ubicacionesCompletas.map((u) =>
          api
            .get(`/pacientes/${u.qr_code}/ruta`)
            .then((r) => ({
              qr: u.qr_code,
              ruta: r.data.ruta || [],
              estado: r.data.estado || null,
              progreso: r.data.progreso ?? 0,
              visitadas: r.data.visitadas || [],
            }))
            .catch(() => ({ qr: u.qr_code, ruta: [], estado: null, progreso: 0, visitadas: [] }))
        )
      );

      const rutasMap = {};
      rutasData.forEach((r) => { rutasMap[r.qr] = r; });

      // ── Salas oficiales ──
      const salasOficiales = new Set(salasDisponibles.map((s) => s.trim().toUpperCase()));
      const salasDisponiblesVacias = salasOficiales.size === 0;

      const now = new Date();

      const pacientesVisibles = ubicacionesCompletas
        .map((paciente) => {
          const rutaInfo = rutasMap[paciente.qr_code] || { ruta: [], estado: null, progreso: 0, visitadas: [] };
          const { ruta, estado } = rutaInfo;
          const fechaReferencia = paciente.timestamp instanceof Date
            ? paciente.timestamp
            : parseTimestamp(paciente.timestamp);

          // 1. Escaneo físico real
          if (paciente.sala && paciente.sala !== 'SIN UBICACIÓN ASIGNADA' && paciente.sala !== 'Ruta Concluida') {
            return { ...paciente, timestamp: fechaReferencia, rutaInfo, esProximaSala: false };
          }

          // 2. Sin escaneo pero ruta activa → inferir próxima sala
          if (ruta.length > 0 && estado && estado !== 'FINALIZADA') {
            return {
              ...paciente,
              sala: ruta[rutaInfo.progreso] || ruta[0],
              timestamp: fechaReferencia,
              esProximaSala: true,
              rutaInfo,
            };
          }

          // 3. Ruta finalizada
          if (estado === 'FINALIZADA') {
            return {
              ...paciente,
              sala: paciente.sala || 'Ruta Concluida',
              timestamp: fechaReferencia,
              esProximaSala: false,
              rutaInfo,
            };
          }

          // 4. Cualquier otro caso con datos mínimos (ruta incompleta/abandonada)
          if (paciente.qr_code && paciente.nombre_paciente) {
            return {
              ...paciente,
              sala: paciente.sala || '—',
              timestamp: fechaReferencia,
              esProximaSala: false,
              rutaInfo,
            };
          }

          return null;
        })
        .filter(Boolean)
        .filter((p) => {
          const hoursSince = (now - p.timestamp) / (1000 * 60 * 60);
          const activo = p.rutaInfo.estado && p.rutaInfo.estado !== 'FINALIZADA';
          const reciente = hoursSince <= VISIBILITY_HOURS;
          const esFinalizadoReciente = p.rutaInfo.estado === 'FINALIZADA' && reciente;

          const salaValida = salasDisponiblesVacias || esFinalizadoReciente
            ? true
            : salasOficiales.has((p.sala || '').trim().toUpperCase());

          return (activo || reciente) && salaValida;
        })
        .sort((a, b) => {
        // Primero los escaneados físicamente (no inferidos)
        if (!a.esProximaSala && b.esProximaSala) return -1;
        if (a.esProximaSala && !b.esProximaSala) return 1;
        // Luego por timestamp más reciente
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      })

      setRutas(rutasMap);
      setUbicacionesFiltradas(pacientesVisibles);
    } catch (err) {
      console.error('Error en fetchData:', err);
      showSnackbar('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Refresco automático cada 10s
  useEffect(() => {
    fetchData();
    if (modoManual) return;
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [modoManual]);

  // Rotación automática de páginas cada 30s
  useEffect(() => {
    const totalPages = Math.ceil(ubicacionesFiltradas.length / ROWS_PER_PAGE);
    if (totalPages <= 1 || modoManual) return;
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, 30000);
    return () => clearInterval(interval);
  }, [ubicacionesFiltradas.length, modoManual]);

  // === HANDLERS MENÚ 3 PUNTOS ===
  const handleMenuOpen = (event, paciente) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuPaciente(paciente);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuPaciente(null);
  };

  const handleAbrirDialogoFinalizar = () => {
    setDialogPaciente(menuPaciente);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleCerrarDialogo = () => {
    if (finalizando) return;
    setDialogOpen(false);
    setDialogPaciente(null);
  };

  const handleConfirmarFinalizar = async () => {
    if (!dialogPaciente) return;
    setFinalizando(true);
    try {
      await api.post('/pacientes/ruta/finalizar', { qr_code: dialogPaciente.qr_code });
      showSnackbar(`Ruta de "${dialogPaciente.nombre_paciente}" finalizada correctamente.`, 'success');
      setDialogOpen(false);
      setDialogPaciente(null);
      await fetchData();
    } catch (err) {
      console.error('Error finalizando ruta:', err);
      showSnackbar('No se pudo finalizar la ruta. Intente de nuevo.', 'error');
    } finally {
      setFinalizando(false);
    }
  };

  const handlePageChange = (_, value) => setCurrentPage(value - 1);

  const totalPages = Math.ceil(ubicacionesFiltradas.length / ROWS_PER_PAGE);
  const pacientesEnPagina = ubicacionesFiltradas.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE
  );

  if (loading && ubicacionesFiltradas.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ENCABEZADO */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Ubicación Actual de Pacientes ({SUCURSAL_NAME})
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={modoManual}
              onChange={(e) => setModoManual(e.target.checked)}
              color="primary"
            />
          }
          label="Modo manual (desactiva rotación y actualización automática)"
        />
      </Box>

      {totalPages > 1 && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          Página {currentPage + 1} de {totalPages} • {ubicacionesFiltradas.length} pacientes visibles
          {' • '}
          {modoManual
            ? 'Actualización y rotación pausadas'
            : 'Se actualiza cada 10s • Cambia página cada 30s'}
        </Typography>
      )}

      {/* TABLA */}
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Sala Actual</strong></TableCell>
              <TableCell><strong>Paciente</strong></TableCell>
              <TableCell><strong>Dueño</strong></TableCell>
              <TableCell><strong>Hora</strong></TableCell>
              <TableCell><strong>Ruta</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {pacientesEnPagina.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No hay pacientes visibles en este momento.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pacientesEnPagina.map((u) => {
                const {
                  ruta = [],
                  estado = null,
                  progreso = 0,
                  visitadas = [],
                } = rutas[u.qr_code] || {};

                const salaActualNormalizada = (u.sala || '').trim().toUpperCase();

                const esActual =
                  ruta.length > 0 &&
                  (ruta[progreso > 0 ? progreso - 1 : 0] || '').trim().toUpperCase() === salaActualNormalizada &&
                  estado !== 'FINALIZADA';

                const puedeFinalizarManualmente =
                  estado && estado !== 'FINALIZADA' && ruta.length > 0;

                return (
                  <TableRow key={`${u.qr_code}-${u.timestamp}`} hover>
                    {/* SALA */}
                    <TableCell>
                      <Chip
                        label={u.esProximaSala ? `${u.sala} (próxima)` : u.sala}
                        size="small"
                        sx={{
                          bgcolor: esActual ? '#ffc107' : u.esProximaSala ? '#9333ea' : '#dbeafe',
                          color: esActual || u.esProximaSala ? '#000' : '#1d4ed8',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>

                    {/* PACIENTE */}
                    <TableCell>
                      <strong>{u.nombre_paciente}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {u.qr_code}
                      </Typography>
                    </TableCell>

                    {/* DUEÑO */}
                    <TableCell>{u.nombre_cliente}</TableCell>

                    {/* HORA */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#059669' }}>
                        <AccessTime sx={{ fontSize: 16, mr: 0.5 }} />
                        {formatTime(u.timestamp)}
                      </Box>
                    </TableCell>

                    {/* RUTA */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {ruta.map((sala, idx) => {
                          const salaRutaNorm = sala.trim().toUpperCase();
                          const yaVisitada =
                            visitadas.some((v) => v.trim().toUpperCase() === salaRutaNorm) ||
                            salaActualNormalizada === salaRutaNorm;

                          const esSiguiente =
                            !yaVisitada &&
                            ruta.slice(0, idx).every((s) => {
                              const sNorm = s.trim().toUpperCase();
                              return (
                                visitadas.some((v) => v.trim().toUpperCase() === sNorm) ||
                                salaActualNormalizada === sNorm
                              );
                            });

                          return (
                            <Chip
                              key={idx}
                              label={sala}
                              size="small"
                              color={esSiguiente ? 'primary' : yaVisitada ? 'success' : 'default'}
                              variant="filled"
                              icon={yaVisitada ? <CheckIcon fontSize="small" /> : null}
                              sx={{
                                fontWeight: esSiguiente ? 'bold' : 'normal',
                                ...(esSiguiente && { border: '3px solid #1976d2', color: 'white' }),
                              }}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>

                    {/* ESTADO */}
                    <TableCell>
                      {estado === 'FINALIZADA' ? (
                        <Chip
                          label="Ruta Finalizada"
                          color="success"
                          size="small"
                          icon={<CheckIcon />}
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : estado ? (
                        <Chip
                          label={estado}
                          color="primary"
                          size="small"
                          sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 'bold' }}
                        />
                      ) : ruta.length > 0 ? (
                        <Chip label="En Progreso" color="warning" size="small" />
                      ) : null}
                    </TableCell>

                    {/* MENÚ ⋮ */}
                    <TableCell align="right" sx={{ pr: 1 }}>
                      {puedeFinalizarManualmente && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, u)}
                          aria-label="Opciones del paciente"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINACIÓN */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            siblingCount={2}
            boundaryCount={1}
          />
        </Box>
      )}

      {/* MENÚ CONTEXTUAL */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleAbrirDialogoFinalizar} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <StopCircleIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Finalizar ruta manualmente</ListItemText>
        </MenuItem>
      </Menu>

      {/* DIÁLOGO CONFIRMAR FINALIZAR */}
      <Dialog open={dialogOpen} onClose={handleCerrarDialogo} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>¿Finalizar ruta manualmente?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Estás a punto de finalizar la ruta de{' '}
            <strong>{dialogPaciente?.nombre_paciente}</strong>
            {dialogPaciente?.nombre_cliente ? ` (dueño: ${dialogPaciente.nombre_cliente})` : ''}.
            <br /><br />
            Esta acción marcará la ruta como <strong>FINALIZADA</strong> y el paciente
            dejará de aparecer en la lista. Úsalo solo si el registro fue un error o el
            paciente está deshabilitado.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCerrarDialogo} disabled={finalizando}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarFinalizar}
            color="error"
            variant="contained"
            disabled={finalizando}
            startIcon={
              finalizando
                ? <CircularProgress size={16} color="inherit" />
                : <StopCircleIcon />
            }
          >
            {finalizando ? 'Finalizando...' : 'Sí, finalizar ruta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UbicacionVista;